import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import moment from 'moment';
import * as React from 'react';
import { Waypoint } from 'react-waypoint';
import { useHasNonProfitIntegration } from '../../../../../models/hooks/useHasNonProfitIntegration';
import { getDisplayName } from '../../../../../models/NameUtils';
import { numberToCurrencyStringValue, numberToUSDCurrencyStringValue } from '../../../../../models/UiUtils';
import { useDonationFilterQuery, useDonationSummaryQuery } from '../../../../../queries';
import { Dropdown, DropdownContent, DropdownEllipsisTrigger, DropdownItem } from '../../../../components-v2/Dropdown';
import { Pill } from '../../../../components-v2/Pill';
import { EditDonationModal } from '../../../dataBoards/donations/EditDonationModal/';
import { HeartIcon } from '../../../svgs/icons/HeartIcon';
import { ROUTES } from '../../../../constants/routes';
import { FullscreenModalNavLink } from '../../../fullscreen/FullscreenModalNavLink';
import { DataBoardRow, DataBoardTable, dataBoardStyles, pillColorFromHex } from '../../DataBoardTable';
import { styleSheet } from './styles';

type EntityDonationsMode = 'contact' | 'company';

export const EntityDonations = ({
	entity,
	mode = 'contact',
	householdsEnabled = false,
}: {
	entity: Api.IEntity;
	mode?: EntityDonationsMode;
	householdsEnabled?: boolean;
}) => {
	const [donationToEdit, setDonationToEdit] = React.useState<Api.IDonation | null>(null);
	const { hasNonProfitIntegration, isPending: isNonProfitIntegrationPending } = useHasNonProfitIntegration();
	const canEdit = !isNonProfitIntegrationPending && !hasNonProfitIntegration;
	const [refreshToken, setRefreshToken] = React.useState(new Date().getTime());
	const isCompanyMode = mode === 'company';

	// A joint gift is recorded against whichever spouse handed it over, so on a contact in a household the
	// board scopes to the household and names the giver on every row instead of implying the viewed contact.
	const contact = isCompanyMode ? null : (entity as Api.IContact);
	const householdId = householdsEnabled ? contact?.household?.id : undefined;
	const isHouseholdRollUp = !!householdId;

	const scopeCriteria: Api.IDonationFilterCriteria = isCompanyMode
		? { property: Api.DonationFilterProperty.CompanyId, value: entity.id }
		: isHouseholdRollUp
			? { property: Api.DonationFilterProperty.HouseholdId, value: householdId }
			: { property: Api.DonationFilterProperty.ContactId, value: entity.id };

	const filterRequest: Api.IDonationFilterRequest = { criteria: [scopeCriteria] };

	const donationFilterQuery = useDonationFilterQuery({
		enabled: !!entity.id,
		filterRequest,
		expand: 'contact,TagsCollection',
	});

	const summaryQuery = useDonationSummaryQuery({
		enabled: (isCompanyMode || isHouseholdRollUp) && !!entity.id,
		filterRequest,
		meta: {
			toastErrorMessage: isCompanyMode
				? 'Failed to load donation totals for this company'
				: 'Failed to load donation totals for this household',
			errorLogging: {
				category: 'EntityDonations',
				action: isCompanyMode ? 'CompanyDonationSummary-Error' : 'HouseholdDonationSummary-Error',
			},
		},
	});

	const donations = React.useMemo(() => {
		return donationFilterQuery.data?.pages.map(page => page.values).flat() ?? [];
	}, [donationFilterQuery.data]);

	const onFetchNextBatchOfDonations = () => {
		if (donationFilterQuery.hasNextPage && !donationFilterQuery.isFetchingNextPage) {
			donationFilterQuery.fetchNextPage();
		}
	};

	// A company is not a valid donor: the donation still has to land on one of its contacts, so the
	// company view opens the modal with no contact prefilled and the picker scoped to that company.
	const addItemClicked = () => {
		setDonationToEdit((isCompanyMode ? {} : { contact: entity }) as Api.IDonation);
	};

	const refreshDonations = () => {
		setRefreshToken(new Date().getTime());
		setDonationToEdit(null);
	};

	const summary = summaryQuery.data;
	const contactCount = summary?.contactCount ?? 0;
	const showRollUpSummary = isCompanyMode && !!summary;
	// Resolves only for a household of exactly two named spouses, so the generic wording has to hold up.
	const householdName = contact?.household?.name;
	const showHouseholdSummary = isHouseholdRollUp && !!summary && !!summary.totalDonations;
	const hasOtherHouseholdDonor = isHouseholdRollUp && donations.some(donation => donation.contact?.id !== entity.id);
	const showDonorName = isCompanyMode || hasOtherHouseholdDonor;
	const boardTitle = isHouseholdRollUp ? `Donations · ${householdName ?? 'Household'}` : 'Donations';

	const donorNameOf = (donation: Api.IDonation) =>
		(donation.contact ? getDisplayName(donation.contact) : donation.donorName) || 'Unknown donor';

	const rows: DataBoardRow[] = donations.map(donation => {
		const date = moment(donation.date).format('MM/DD/YYYY');
		return {
			id: donation.id,
			name:
				isCompanyMode && donation.contact?.id ? (
					<FullscreenModalNavLink
						className={css(dataBoardStyles.link)}
						to={`${ROUTES.contacts.people}/${donation.contact.id}`}
					>
						{donorNameOf(donation)}
					</FullscreenModalNavLink>
				) : showDonorName ? (
					donorNameOf(donation)
				) : (
					date
				),
			statusTitle: donation.campaign?.name,
			status: donation.campaign ? (
				<Pill
					variant='label'
					color={pillColorFromHex(donation.campaign.foregroundColor)}
					label={donation.campaign.name}
				/>
			) : null,
			fields: showDonorName ? [date] : [],
			value: `$${numberToCurrencyStringValue(donation.amount)}`,
			details: donation.details ? [{ label: 'Details', value: donation.details }] : undefined,
			actions: canEdit ? (
				<Dropdown>
					<DropdownEllipsisTrigger aria-label={`Actions for donation from ${donorNameOf(donation)}`} />
					<DropdownContent>
						<DropdownItem onClick={() => setDonationToEdit(donation)}>Edit</DropdownItem>
					</DropdownContent>
				</Dropdown>
			) : undefined,
		};
	});

	const summaryText = showRollUpSummary
		? // The roll-up only covers contacts linked to this company, and the contact set is
			// ACL-scoped to the viewer, so the total is described by what it actually spans.
			// A truncated fan-out gets no total at all rather than a misleadingly partial one.
			summary.contactsTruncated
			? `Showing donations from the first ${contactCount} contacts at this company.`
			: `Total from ${contactCount} ${contactCount === 1 ? 'contact' : 'contacts'} at this company: ${numberToUSDCurrencyStringValue(summary.totalAmount ?? 0)}`
		: showHouseholdSummary
			? `Household total: ${numberToUSDCurrencyStringValue(summary.totalAmount ?? 0)} across ${summary.totalDonations} ${
					summary.totalDonations === 1 ? 'gift' : 'gifts'
				}. Largest gift: ${numberToUSDCurrencyStringValue(summary.largestDonation ?? 0)}`
			: undefined;

	// Showing a total and "no donations exist" at once reads as a contradiction.
	const emptyText = summaryText
		? undefined
		: donationFilterQuery.isError
			? 'Couldn’t load donations.'
			: isCompanyMode
				? 'No donations exist for this company.'
				: isHouseholdRollUp
					? 'No donations exist for this household.'
					: 'No donations exist for this contact.';

	return (
		<div key={refreshToken}>
			<DataBoardTable
				icon={<HeartIcon height={20} width={20} className={css(styleSheet.icon)} />}
				iconColor='pink'
				title={boardTitle}
				summary={summaryText}
				addItemName={isNonProfitIntegrationPending ? undefined : 'Donation'}
				addDisabledReason={
					hasNonProfitIntegration
						? 'Donations sync from your donor management integration, so add them there.'
						: undefined
				}
				onAdd={addItemClicked}
				nameLabel={showDonorName ? 'Donor' : 'Date'}
				statusLabel='Campaign'
				fieldLabels={showDonorName ? ['Date'] : []}
				valueLabel='Amount'
				rows={rows}
				isLoading={donationFilterQuery.isPending}
				emptyText={emptyText}
			>
				{donations.length ? <Waypoint bottomOffset='-200px' onEnter={onFetchNextBatchOfDonations} /> : null}
			</DataBoardTable>

			{donationToEdit ? (
				<EditDonationModal
					initialDonation={donationToEdit}
					restrictToCompanyId={isCompanyMode ? entity.id : undefined}
					isOpen={!!donationToEdit}
					setIsOpen={open => !open && setDonationToEdit(null)}
					onSave={refreshDonations}
					onCancel={refreshDonations}
				/>
			) : null}
		</div>
	);
};
